import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import ProductList from 'containers/ProductList';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useRouter } from 'found';
import DiscoverPage from './Page';
import { DISCOVER_PRODUCT_LIST_INFO } from '../../schema/apolloQueries/discoverQueries';
import InfiniteScrollApp from '../../components/InfiniteScrollerApollo';
import { GET_PROFILE_INFO } from '../../schema/apolloQueries/commonQueries';
import { LIKED_UNLIKED_SPONSOR } from '../../schema/apolloMutations/eventDeatilsMutations';
import { useAudienceTrackerContext } from '../../lib/trackers/Audience';

const pageSize = 20;

const DiscoverProductsPage = () => {
  const { match: { location: { pathname } } } = useRouter();
  const { trackView } = useAudienceTrackerContext();
  const [resetPage, setResetPage] = useState(0);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [filter, setFilter] = useState('all');

  const { data, fetchMore } = useQuery(DISCOVER_PRODUCT_LIST_INFO, {
    variables: {
      pageNo: 1,
      pageSize,
      slug: encodeURIComponent(filter),
    },
  });

  const [handleLike] = useMutation(LIKED_UNLIKED_SPONSOR);

  const client = useApolloClient();

  const profileData = client.readQuery({
    query: GET_PROFILE_INFO,
  });

  useEffect(() => {
    if (Array.isArray(data?.discoverProducts?.data)) {
      setIsFirstLoad(false);
    } else {
      setIsFirstLoad(true);
    }
  }, [data]);

  const handleFetchMore = (isPage, setHasMore) => {
    return new Promise((reslove, reject) => {
      fetchMore({
        variables: {
          pageNo: isPage,
          pageSize,
          slug: encodeURIComponent(filter),
        },
        updateQuery: (pre, { fetchMoreResult }) => {
          if (
            pre?.discoverProducts.data?.length
            >= fetchMoreResult?.discoverProducts?.totalCount
          ) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
          return pre?.discoverProducts.data?.length
            >= fetchMoreResult?.discoverProducts?.totalCount
            ? pre
            : {
                discoverProducts: {
                  __typename: pre.discoverProducts.__typename,
                  totalCount: pre.discoverProducts.totalCount,
                  data: [
                    ...pre.discoverProducts.data,
                    ...fetchMoreResult.discoverProducts.data,
                  ],
                },
              };
        },
      })
        .then(() => {
          reslove();
        })
        .catch(() => {
          reject();
        });
    });
  };

  const handleCategoryChange = event => {
    setIsFirstLoad(true);
    setResetPage(1);
    setFilter(event.target.value);
    fetchMore({
      variables: {
        pageNo: 1,
        pageSize,
        slug: encodeURIComponent(event.target.value),
      },
      updateQuery: (_pre, { fetchMoreResult }) => {
        return fetchMoreResult;
      },
    });
  };

  useEffect(() => {
    trackView('discover_products', {
        page_name: 'Products',
        page_url: pathname,
      });
  }, []);

  return (
    <DiscoverPage
      value={filter}
      category={data?.allEventCategories}
      onCategoryChange={handleCategoryChange}
      title='Products'
      viewer={profileData?.getProfile}
    >
      <InfiniteScrollApp
        getPage={resetPage}
        handleFetchMore={handleFetchMore}
        isFirstLoad={isFirstLoad}
        doneText='Be sure to check back soon for new products to discover!'
        isEmpty={data?.discoverProducts?.data?.length === 0}
        emptyText='Check back soon for new products'
        emptyTitle='There are no products right now!'
      >
        <ProductList
          handleLike={handleLike}
          data={data}
          viewer={profileData?.getProfile}
        />
      </InfiniteScrollApp>
    </DiscoverPage>
  );
};

DiscoverProductsPage.propTypes = {
  viewer: PropTypes.object.isRequired,
};

export default DiscoverProductsPage;
